import React from "react";
import { Box, Typography, Container, Link } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { withRouter } from "react-router-dom";

const styles = (theme) => ({
  wrapper: {
    minHeight: "100vh",
    backgroundColor: theme.palette.primary.background,
    padding: theme.spacing(4, 0),
  },
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: theme.spacing(0, 3),
  },
  header: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  logo: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  content: {
    "& h2": {
      color: theme.palette.common.text,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      fontSize: "1.5rem",
      fontWeight: "600",
    },
    "& h3": {
      color: theme.palette.common.text,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      fontSize: "1.2rem",
      fontWeight: "500",
    },
    "& p": {
      color: theme.palette.common.text,
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
    "& ul": {
      color: theme.palette.common.text,
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    },
    "& li": {
      marginBottom: theme.spacing(1),
    },
    "& .important": {
      fontWeight: "600",
      color: theme.palette.primary.main,
    },
    "& .warning": {
      backgroundColor: theme.palette.mode === "light" ? "#fff3cd" : "#332701",
      border: `1px solid ${theme.palette.mode === "light" ? "#ffeaa7" : "#664d03"}`,
      borderRadius: "4px",
      padding: theme.spacing(2),
      margin: theme.spacing(2, 0),
    },
  },
  backLink: {
    display: "inline-block",
    marginTop: theme.spacing(4),
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

const TermsOfService = ({ classes, theme, history }) => {
  return (
    <Box className={classes.wrapper}>
      <Container className={classes.container}>
        <div className={classes.logo}>
          <Link href="/" style={{ textDecoration: "none" }}>
            <img
              src={
                theme.palette.mode === "light"
                  ? "/images/Auralyze_ai_black_logo.png"
                  : "/images/auralyze_logo_white.png"
              }
              alt="Auralyze Logo"
              style={{ 
                maxWidth: "200px", 
                height: "auto", 
                display: "block",
                margin: "0 auto"
              }}
            />
          </Link>
        </div>

        <div className={classes.header}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            sx={{ color: theme.palette.common.text }}
          >
            Terms of Service
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mt: 2,
              color: theme.palette.common.text,
              opacity: 0.7
            }}
          >
            Last updated: 15th January 2025
          </Typography>
        </div>

        <div className={classes.content}>
          <div className="warning">
            <Typography component="p" className="important">
              IMPORTANT: Please read these Terms of Service carefully before using our platform. By accessing or using Auralyze.ai, you agree to be bound by these terms and our Privacy Policy.
            </Typography>
          </div>

          <Typography component="h2">1. Acceptance of Terms</Typography>
          <Typography component="p">
            These Terms of Service ("Terms", "Agreement") constitute a legally binding agreement between you ("User", "you", "your") and Auralyze Limited ("Auralyze", "Company", "we", "us", "our"), a company incorporated under the laws of England and Wales. By accessing, browsing, or using the Auralyze.ai platform ("Service", "Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms and all applicable laws and regulations.
          </Typography>
          <Typography component="p">
            <span className="important">IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE OUR SERVICE.</span>
          </Typography>

          <Typography component="h2">2. Description of Service</Typography>
          <Typography component="p">
            Auralyze.ai is an artificial intelligence-powered platform that provides comprehensive interview preparation, career guidance, and professional development services. Our services include but are not limited to:
          </Typography>
          <ul>
            <li>AI-powered mock interviews with real-time feedback</li>
            <li>Personalised interview questions and scenarios</li>
            <li>Video recording and analysis capabilities</li>
            <li>Career coaching and guidance tools</li>
            <li>CV optimisation and review services</li>
            <li>Professional development resources and content</li>
            <li>Performance analytics and progress tracking</li>
          </ul>

          <Typography component="h2">3. Data Collection, Storage, and Processing</Typography>
          
          <Typography component="h3">3.1 Types of Data We Collect</Typography>
          <Typography component="p">
            We collect and process the following categories of personal data:
          </Typography>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, password (encrypted), profile information</li>
            <li><strong>Video Recordings:</strong> Interview practice sessions, responses, and related metadata</li>
            <li><strong>Audio Data:</strong> Voice recordings during interview sessions</li>
            <li><strong>Usage Data:</strong> Platform interactions, session duration, feature usage, performance metrics</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies, and similar technologies</li>
            <li><strong>Communication Data:</strong> Support requests, feedback, and correspondence with our team</li>
            <li><strong>Payment Information:</strong> Billing details, transaction history (processed through secure third-party providers)</li>
          </ul>

          <Typography component="h3">3.2 Data Storage Infrastructure</Typography>
          <Typography component="p">
            <span className="important">Your data is stored securely using cloud infrastructure:</span>
          </Typography>
          <ul>
            <li><strong>Primary Storage:</strong> Amazon Web Services (AWS) cloud storage</li>
            <li><strong>Video Storage:</strong> AWS S3 with server-side encryption</li>
            <li><strong>Database Security:</strong> Encrypted databases with regular backups</li>
            <li><strong>Access Controls:</strong> Restricted access with authentication for our team</li>
          </ul>

          <Typography component="h3">3.3 Data Processing Purposes</Typography>
          <Typography component="p">
            We process your personal data for the following legitimate purposes:
          </Typography>
          <ul>
            <li>Providing and improving our AI-powered interview preparation services</li>
            <li>Analysing your performance to deliver personalised feedback and recommendations</li>
            <li>Training and improving our artificial intelligence algorithms (using anonymised data)</li>
            <li>Ensuring platform security and preventing fraud or abuse</li>
            <li>Communicating with you about your account and our services</li>
            <li>Complying with legal obligations and regulatory requirements</li>
            <li>Conducting research and analytics to enhance user experience</li>
          </ul>

          <Typography component="h3">3.4 Data Retention</Typography>
          <Typography component="p">
            We retain your personal data only as long as necessary for the purposes outlined in these Terms:
          </Typography>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained whilst your account remains active</li>
            <li><strong>Inactive Accounts:</strong> Data deleted after 24 months of inactivity (with 30-day notice)</li>
            <li><strong>Video Recordings:</strong> Retained for up to 3 years or until account deletion</li>
            <li><strong>Legal Requirements:</strong> Some data may be retained longer to comply with legal obligations</li>
            <li><strong>Anonymised Data:</strong> May be retained indefinitely for research and service improvement</li>
          </ul>

          <Typography component="h2">4. Privacy Rights and GDPR Compliance</Typography>
          
          <Typography component="h3">4.1 Your Privacy Rights</Typography>
          <Typography component="p">
            Under applicable data protection laws, including the General Data Protection Regulation (GDPR), you have the following rights:
          </Typography>
          <ul>
            <li><strong>Right of Access:</strong> Request copies of your personal data</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Right to Restrict Processing:</strong> Request limitation of how we process your data</li>
            <li><strong>Right to Data Portability:</strong> Request transfer of your data in a machine-readable format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for processing where applicable</li>
          </ul>

          <Typography component="h3">4.2 Exercising Your Rights</Typography>
          <Typography component="p">
            To exercise any of these rights, contact us at <strong>support@auralyze.ai</strong>. We will respond within 30 days and may require identity verification. Some requests may be subject to legal limitations or exceptions.
          </Typography>

          <Typography component="h2">5. User Accounts and Security</Typography>
          
          <Typography component="h3">5.1 Account Registration</Typography>
          <Typography component="p">
            To access our services, you must create an account and provide accurate, complete information. You agree to:
          </Typography>
          <ul>
            <li>Provide truthful and accurate registration information</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your login credentials secure and confidential</li>
            <li>Notify us immediately of any unauthorised account access</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <Typography component="h3">5.2 Account Sharing Prohibition</Typography>
          <div className="warning">
            <Typography component="p" className="important">
              STRICT PROHIBITION: Account sharing is absolutely forbidden. Each account is licenced for individual use only.
            </Typography>
          </div>
          <Typography component="p">
            You may NOT:
          </Typography>
          <ul>
            <li>Share your username, password, or account access with any other person</li>
            <li>Allow multiple individuals to use the same account</li>
            <li>Create accounts on behalf of other individuals without authorisation</li>
            <li>Transfer, sell, or assign your account to another person</li>
            <li>Use automated systems or bots to access the platform</li>
          </ul>
          <Typography component="p">
            <span className="important">Violation of this policy will result in immediate account termination without refund.</span>
          </Typography>

          <Typography component="h2">6. Acceptable Use Policy</Typography>
          <Typography component="p">
            You agree to use our Service responsibly and in compliance with all applicable laws. Prohibited activities include:
          </Typography>
          <ul>
            <li>Violating any local, national, or international laws or regulations</li>
            <li>Transmitting any harmful, threatening, abusive, defamatory, or illegal content</li>
            <li>Attempting to gain unauthorised access to our systems or other users' accounts</li>
            <li>Interfering with or disrupting the Service, servers, or networks</li>
            <li>Using the Service for any commercial purpose without explicit written consent</li>
            <li>Reverse engineering, decompiling, or attempting to extract source code</li>
            <li>Uploading malicious software, viruses, or harmful code</li>
            <li>Impersonating others or providing false identity information</li>
            <li>Collecting or harvesting personal information from other users</li>
            <li>Using the platform to discriminate, harass, or harm others</li>
          </ul>

          <Typography component="h2">7. Intellectual Property Rights</Typography>
          
          <Typography component="h3">7.1 Our Intellectual Property</Typography>
          <Typography component="p">
            The Service, including all content, features, functionality, software, algorithms, and technology, is owned by Auralyze and protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. This includes:
          </Typography>
          <ul>
            <li>AI algorithms and machine learning models</li>
            <li>Platform software and user interfaces</li>
            <li>Content, text, graphics, logos, and multimedia</li>
            <li>Trademarks, service marks, and brand elements</li>
            <li>Proprietary methodologies and processes</li>
          </ul>

          <Typography component="h3">7.2 User-Generated Content</Typography>
          <Typography component="p">
            You retain ownership of content you create (videos, responses, etc.), but grant us a worldwide, royalty-free licence to use, process, and analyse this content to provide and improve our services. This licence includes the right to:
          </Typography>
          <ul>
            <li>Process your content through our AI systems for feedback generation</li>
            <li>Use anonymised and aggregated data for service improvement</li>
            <li>Store and backup your content for service delivery</li>
            <li>Display your content back to you within the platform</li>
          </ul>

          <Typography component="h2">8. Payment Terms and Billing</Typography>
          
          <Typography component="h3">8.1 Subscription Fees</Typography>
          <Typography component="p">
            Access to premium features requires a paid subscription. By subscribing, you agree to pay all applicable fees as described on our pricing page. Fees are:
          </Typography>
          <ul>
            <li>Charged in advance on a recurring basis (monthly or annually)</li>
            <li>Non-refundable except as required by law or our refund policy</li>
            <li>Subject to change with 30 days' notice to existing subscribers</li>
            <li>Exclusive of applicable taxes, which are your responsibility</li>
          </ul>

          <Typography component="h3">8.2 Automatic Renewal</Typography>
          <Typography component="p">
            Subscriptions automatically renew unless cancelled before the renewal date. You can cancel anytime through your account settings or by contacting support.
          </Typography>

          <Typography component="h2">9. Disclaimers and Limitation of Liability</Typography>
          
          <Typography component="h3">9.1 Service Disclaimers</Typography>
          <div className="warning">
            <Typography component="p" className="important">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
            </Typography>
          </div>
          <Typography component="p">
            We disclaim all warranties, express or implied, including:
          </Typography>
          <ul>
            <li>Merchantability, fitness for a particular purpose, and non-infringement</li>
            <li>Accuracy, reliability, or completeness of AI-generated feedback</li>
            <li>Uninterrupted or error-free service operation</li>
            <li>Security of data transmission over the internet</li>
            <li>Specific outcomes or results from using our service</li>
          </ul>

          <Typography component="h3">9.2 Limitation of Liability</Typography>
          <Typography component="p">
            <span className="important">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</span>
          </Typography>
          <ul>
            <li>Our total liability shall not exceed the amount you paid in the 12 months preceding the claim</li>
            <li>We are not liable for indirect, incidental, special, consequential, or punitive damages</li>
            <li>We are not responsible for data loss, business interruption, or lost profits</li>
            <li>Our liability is limited regardless of the legal theory (contract, tort, negligence, etc.)</li>
            <li>Some jurisdictions may not allow these limitations, so they may not apply to you</li>
          </ul>

          <Typography component="h2">10. Indemnification</Typography>
          <Typography component="p">
            You agree to indemnify, defend, and hold harmless Auralyze, its officers, directors, employees, agents, and affiliates from any claims, damages, losses, or expenses (including legal fees) arising from:
          </Typography>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Violation of these Terms or applicable laws</li>
            <li>Infringement of third-party rights</li>
            <li>Content you submit or transmit through the Service</li>
            <li>Your negligent or wrongful conduct</li>
          </ul>

          <Typography component="h2">11. Termination</Typography>
          
          <Typography component="h3">11.1 Termination by You</Typography>
          <Typography component="p">
            You may terminate your account at any time by contacting support or using account deletion features. Upon termination:
          </Typography>
          <ul>
            <li>Your access to the Service will cease immediately</li>
            <li>Your data will be deleted according to our retention policy</li>
            <li>No refunds will be provided for unused subscription periods</li>
          </ul>

          <Typography component="h3">11.2 Termination by Us</Typography>
          <Typography component="p">
            We may suspend or terminate your account immediately for:
          </Typography>
          <ul>
            <li>Violation of these Terms or our policies</li>
            <li>Fraudulent or illegal activity</li>
            <li>Non-payment of fees</li>
            <li>Extended periods of inactivity</li>
            <li>Any reason at our sole discretion with notice</li>
          </ul>

          <Typography component="h2">12. International Users and Data Transfers</Typography>
          <Typography component="p">
            Our Service is operated from the United Kingdom. By using our Service, you consent to the transfer of your data to jurisdictions where our service providers operate. We implement appropriate safeguards for international data transfers, including:
          </Typography>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions where applicable</li>
            <li>Security measures for data protection</li>
          </ul>

          <Typography component="h2">13. Dispute Resolution</Typography>
          
          <Typography component="h3">13.1 Governing Law</Typography>
          <Typography component="p">
            These Terms are governed by the laws of England and Wales, without regard to conflict of law principles.
          </Typography>

          <Typography component="h3">13.2 Dispute Resolution Process</Typography>
          <Typography component="p">
            Before initiating legal proceedings, you agree to attempt resolution through:
          </Typography>
          <ul>
            <li>Direct negotiation with our support team</li>
            <li>Mediation through a mutually agreed mediator</li>
            <li>Arbitration if mediation fails (for disputes over £10,000)</li>
          </ul>

          <Typography component="h3">13.3 Jurisdiction</Typography>
          <Typography component="p">
            Any legal proceedings shall be brought exclusively in the courts of England and Wales.
          </Typography>

          <Typography component="h2">14. Changes to Terms</Typography>
          <Typography component="p">
            We reserve the right to modify these Terms at any time. Material changes will be communicated through:
          </Typography>
          <ul>
            <li>Email notification to registered users</li>
            <li>Prominent notice on our website</li>
            <li>In-app notifications</li>
          </ul>
          <Typography component="p">
            Continued use of the Service after changes constitutes acceptance of the modified Terms. If you disagree with changes, you must stop using the Service and may terminate your account.
          </Typography>

          <Typography component="h2">15. Miscellaneous Provisions</Typography>
          
          <Typography component="h3">15.1 Entire Agreement</Typography>
          <Typography component="p">
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Auralyze regarding the Service.
          </Typography>

          <Typography component="h3">15.2 Severability</Typography>
          <Typography component="p">
            If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.
          </Typography>

          <Typography component="h3">15.3 Waiver</Typography>
          <Typography component="p">
            Our failure to enforce any provision does not constitute a waiver of that provision or any other provision.
          </Typography>

          <Typography component="h3">15.4 Assignment</Typography>
          <Typography component="p">
            You may not assign these Terms without our written consent. We may assign these Terms without restriction.
          </Typography>

          <Typography component="h2">16. Contact Information</Typography>
          <Typography component="p">
            For questions about these Terms of Service, please contact us at <strong>support@auralyze.ai</strong>.
          </Typography>

          <div className="warning">
            <Typography component="p" className="important">
              By using Auralyze.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
            </Typography>
          </div>
        </div>

        <Link 
          className={classes.backLink}
          onClick={() => history.push("/auth/register")}
          style={{ cursor: "pointer" }}
        >
          ← Back to Sign Up
        </Link>
      </Container>
    </Box>
  );
};

export default withRouter(withStyles(styles, { withTheme: true })(TermsOfService)); 